// Fábricas de capas nuevas (posición/tamaño en % del lienzo).

import type { ImageLayer, Layer, ShapeKind, ShapeLayer, TextLayer } from '../scene/types';
import { DEFAULT_FONT_FAMILY } from './fonts';

/** Carga una imagen solo para leer su ratio natural (w/h). Nunca rechaza. */
export function loadImageRatio(url: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve(img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1);
    img.onerror = () => resolve(1);
    img.src = url;
  });
}

export function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `l_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

export function makeText(text: string): TextLayer {
  return {
    id: uid(),
    type: 'text',
    text: text || 'Texto',
    xPct: 50,
    yPct: 50,
    wPct: 76,
    hPct: 16,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSizePct: 8,
    fontWeight: 'bold',
    fontStyle: 'normal',
    color: '#111111',
    align: 'center',
  };
}

/** Clona una capa con id nuevo + un offset chico (para duplicar / copiar-pegar). */
export function cloneLayer(layer: Layer, offset = 3): Layer {
  return { ...layer, id: uid(), xPct: layer.xPct + offset, yPct: layer.yPct + offset } as Layer;
}

export function makeShape(kind: ShapeKind, canvasRatio = 1): ShapeLayer {
  const base = {
    id: uid(),
    type: 'shape' as const,
    kind,
    xPct: 50,
    yPct: 50,
    fill: '#1644EB', // hex-de-marca-ok: default de una forma del editor gráfico, es contenido
    stroke: '#1644EB', // hex-de-marca-ok: default de una forma del editor gráfico, es contenido
    strokeWidthPct: 0,
    radiusPct: 0,
    strokeStyle: 'solid' as const,
  };
  // hPct = wPct * ratio → el círculo/cuadrado se ven regulares sin importar la
  // proporción del lienzo: wPct es % del ancho, hPct % del alto.
  if (kind === 'line') return { ...base, wPct: 40, hPct: 10, fill: 'transparent', strokeWidthPct: 1.2 };
  if (kind === 'arrow') return { ...base, wPct: 42, hPct: 42 * canvasRatio * 0.55 };
  if (kind === 'circle') return { ...base, wPct: 26, hPct: 26 * canvasRatio };
  // rect, triangle, pentagon, hexagon, star → caja cuadrada
  return { ...base, wPct: 30, hPct: 30 * canvasRatio };
}

// Al agregar una imagen, la caja arranca a ~40% de ancho manteniendo el ratio
// natural (para que `object-fit: cover` no recorte). hPct se deriva del ratio del
// lienzo para que se vea con la proporción real de la imagen.
export function makeImage(src: string, canvasRatio: number, naturalRatio: number): ImageLayer {
  const wPct = 40;
  const hPct = Math.min(90, (wPct * canvasRatio) / (naturalRatio || 1));
  return {
    id: uid(),
    type: 'image',
    src,
    xPct: 50,
    yPct: 50,
    wPct,
    hPct,
    radiusPct: 0,
    naturalRatio: naturalRatio || 1,
  };
}
